import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  format,
  formatDistanceToNow,
  differenceInDays,
  isToday,
  isTomorrow,
} from "date-fns";
import { fr } from "date-fns/locale";
import {
  Inbox,
  CalendarCheck,
  TrendingUp,
  TrendingDown,
  Star,
  Wallet,
  Users,
  Utensils,
  ArrowRight,
  ChevronRight,
  MessageCircle,
} from "lucide-react";
import { chefQueries } from "@/domain/chef/chef.query";
import { chefQuoteRequestQueries } from "@/domain/chef-quote-request/chef-quote-request.query";
import { bookingQueries } from "@/domain/booking/booking.query";
import { menuQueries } from "@/domain/menu/menu.query";
import { availabilityQueries } from "@/domain/availability/availability.query";
import { BookingStatuses, OrderBy } from "@hometoq/backend/enums";
import type { Booking } from "@/domain/booking/booking.type";
import { cn } from "@/lib/utils";
import { RevenueChart } from "./-components/RevenueChart";
import { ToqueCard } from "./-components/ToqueCard";

export const Route = createFileRoute("/dashboard/chef/")({
  component: RouteComponent,
});

type Feedback = {
  id: number;
  rating: number;
  comment: string;
  createdAt: string;
  customerFirstName: string;
};

function RouteComponent() {
  const { data: chef, isLoading: chefLoading } = useQuery(chefQueries.me());
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery(
    chefQueries.dashboard()
  );
  const { data: quoteRequests } = useQuery(chefQuoteRequestQueries.myList());
  const { data: nextBookingData } = useQuery(
    bookingQueries.list({
      name: "",
      priceSort: null,
      dateSort: OrderBy.ASC,
      date: null,
      statusSort: null,
      status: BookingStatuses.ACCEPTED,
      page: 1,
    })
  );
  const { data: feedbacks } = useQuery(chefQueries.recentFeedbacks());
  const { data: chefMenus } = useQuery(menuQueries.chefMenus());
  const { data: myAvailabilities } = useQuery(
    availabilityQueries.myAvailabilities(format(new Date(), "yyyy-MM-dd")),
  );

  const isLoading = chefLoading || dashboardLoading;

  const pendingCount =
    quoteRequests?.filter((r) => r.status === "pending").length ?? 0;
  const inFlightCount =
    quoteRequests?.filter(
      (r) => r.status === "proposal_sent" || r.status === "validated",
    ).length ?? 0;

  const nextBooking = nextBookingData?.data?.[0] as Booking | undefined;

  const monthlyRevenue = dashboardData?.monthlyRevenue ?? [];
  const currentMonthRevenue = monthlyRevenue[0]?.revenue ?? 0;
  const lastMonthRevenue = monthlyRevenue[1]?.revenue ?? 0;
  const revenueDelta = useMemo(() => {
    if (lastMonthRevenue === 0) return null;
    const pct =
      ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100;
    return Math.round(pct);
  }, [currentMonthRevenue, lastMonthRevenue]);

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
        <div className="h-32 rounded-3xl bg-white border border-border animate-pulse" />
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 h-64 rounded-3xl bg-white border border-border animate-pulse" />
          <div className="lg:col-span-2 h-64 rounded-3xl bg-white border border-border animate-pulse" />
        </div>
        <div className="h-64 rounded-3xl bg-white border border-border animate-pulse" />
      </div>
    );
  }

  return (
    <section className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      <Hero firstName={chef?.firstName ?? ""} pendingCount={pendingCount} />

      <ToqueCard
        chef={chef}
        menuCount={chefMenus?.length ?? 0}
        availabilityCount={myAvailabilities?.length ?? 0}
        feedbacks={feedbacks}
      />

      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <NextBookingCard booking={nextBooking} />
        </div>
        <div className="lg:col-span-2">
          <StatsGrid
            revenueCurrentMonth={currentMonthRevenue}
            revenueDelta={revenueDelta}
            upcomingBookings={dashboardData?.upcomingBookings ?? 0}
            pendingDemandes={pendingCount}
            proposalSent={inFlightCount}
            averageRating={dashboardData?.averageRating ?? 0}
            reviewsCount={feedbacks?.length ?? 0}
          />
        </div>
      </div>

      {monthlyRevenue.length > 0 && <RevenueChart data={monthlyRevenue} />}

      <RecentReviews feedbacks={(feedbacks as Feedback[] | undefined) ?? []} />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Hero
// ─────────────────────────────────────────────────────────────────────────

function Hero({
  firstName,
  pendingCount,
}: {
  firstName: string;
  pendingCount: number;
}) {
  return (
    <header className="relative overflow-hidden rounded-3xl border border-primary/10 p-6 md:p-8 bg-gradient-to-br from-salmon/45 via-[#fbf6f3] to-white">
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 w-40 h-40 rounded-full bg-primary/5 blur-3xl" />

      <div className="relative flex flex-col lg:flex-row lg:items-center gap-5 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/80">
              Espace chef
            </span>
            <span className="w-1 h-1 rounded-full bg-primary" />
            <span className="text-[11px] text-muted-foreground capitalize">
              {format(new Date(), "EEEE d MMMM", { locale: fr })}
            </span>
          </div>
          <h1 className="font-h1 text-dark-blue mb-1">
            Bonjour {firstName} <span className="inline-block">👋</span>
          </h1>
          <p className="text-sm text-muted-foreground max-w-md">
            {pendingCount > 0
              ? `${pendingCount} demande${pendingCount > 1 ? "s" : ""} ${pendingCount > 1 ? "vous attendent" : "vous attend"} — un client compte sur vous.`
              : "Vous êtes à jour, rien ne traîne dans votre boîte. Bonne journée 🍽️"}
          </p>
        </div>

        {pendingCount > 0 && (
          <Link
            to="/dashboard/chef/reservations"
            search={{ tab: "a-traiter" }}
            className="group inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-primary text-white shadow-[0_8px_24px_-8px_rgba(232,89,60,0.5)] hover:shadow-[0_8px_28px_-6px_rgba(232,89,60,0.65)] transition-shadow shrink-0"
          >
            <span className="relative flex">
              <span className="absolute inset-0 rounded-xl bg-white/30 animate-ping" />
              <span className="relative w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
                <Inbox size={18} />
              </span>
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                À traiter
              </span>
              <span className="text-sm font-semibold">Voir les demandes</span>
            </span>
            <ArrowRight
              size={18}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </Link>
        )}
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Next booking
// ─────────────────────────────────────────────────────────────────────────

function NextBookingCard({ booking }: { booking?: Booking }) {
  if (!booking) {
    return (
      <div className="h-full min-h-[280px] rounded-3xl border-2 border-dashed border-border bg-white p-8 flex flex-col items-center justify-center text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center mb-3">
          <CalendarCheck size={22} className="text-primary/60" />
        </div>
        <h3 className="font-h4 text-dark-blue mb-1">
          Aucune prestation à venir
        </h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          Vos prochaines réservations confirmées s'afficheront ici.
        </p>
      </div>
    );
  }

  const eventDate = new Date(booking.date);
  const daysUntil = differenceInDays(eventDate, new Date());
  const dayLabel = isToday(eventDate)
    ? "Aujourd'hui"
    : isTomorrow(eventDate)
      ? "Demain"
      : daysUntil > 0
        ? `Dans ${daysUntil} jour${daysUntil > 1 ? "s" : ""}`
        : "Aujourd'hui";

  const formattedDate = format(eventDate, "EEEE d MMMM yyyy", { locale: fr });
  const totalPriceCents = (booking as any).totalPrice ?? 0;
  const price = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(totalPriceCents / 100);

  return (
    <article className="h-full bg-white rounded-3xl border border-border overflow-hidden flex flex-col">
      <div className="relative px-5 py-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-white/20 text-[10px] font-bold uppercase tracking-wider">
          Confirmée
        </div>
        <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">
          Prochaine prestation
        </p>
        <p className="text-xl font-bold mt-1 leading-tight">{dayLabel}</p>
        <p className="text-sm opacity-90 capitalize">{formattedDate}</p>
      </div>

      <div className="p-5 flex-1 flex flex-col gap-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <NextBookingDetail
            icon={<Users size={16} />}
            label="Client"
            value={`${booking.bookingContact.firstName} ${booking.bookingContact.lastName}`}
          />
          {booking.menu?.name && (
            <NextBookingDetail
              icon={<Utensils size={16} />}
              label="Menu"
              value={booking.menu.name}
            />
          )}
          <NextBookingDetail
            icon={<Wallet size={16} />}
            label="Montant"
            value={price}
          />
        </div>

        <Link
          to="/dashboard/chef/reservations"
          search={{ tab: "a-venir" }}
          className="mt-auto group flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border border-border hover:border-primary/40 hover:bg-salmon/25 transition-colors"
        >
          <span className="text-sm font-semibold text-dark-blue">
            Voir toutes mes prestations
          </span>
          <ChevronRight
            size={16}
            className="text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all"
          />
        </Link>
      </div>
    </article>
  );
}

function NextBookingDetail({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3 min-w-0">
      <span className="w-9 h-9 rounded-xl bg-salmon/40 text-primary flex items-center justify-center shrink-0">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p className="text-sm font-semibold text-dark-blue truncate">
          {value}
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Stats grid
// ─────────────────────────────────────────────────────────────────────────

function StatsGrid({
  revenueCurrentMonth,
  revenueDelta,
  upcomingBookings,
  pendingDemandes,
  proposalSent,
  averageRating,
  reviewsCount,
}: {
  revenueCurrentMonth: number;
  revenueDelta: number | null;
  upcomingBookings: number;
  pendingDemandes: number;
  proposalSent: number;
  averageRating: number;
  reviewsCount: number;
}) {
  const formattedRevenue = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(revenueCurrentMonth / 100);

  return (
    <div className="grid grid-cols-2 gap-3 h-full">
      <Stat
        icon={<Wallet size={16} />}
        label="Revenu du mois"
        value={formattedRevenue}
        accent="primary"
        footer={
          revenueDelta !== null && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-[10px] font-bold",
                revenueDelta >= 0 ? "text-emerald-700" : "text-red-600"
              )}
            >
              {revenueDelta >= 0 ? (
                <TrendingUp size={10} />
              ) : (
                <TrendingDown size={10} />
              )}
              {Math.abs(revenueDelta)}% vs mois dernier
            </span>
          )
        }
      />
      <Stat
        icon={<CalendarCheck size={16} />}
        label="À venir"
        value={upcomingBookings.toString()}
        accent="emerald"
        footer={
          <span className="text-[10px] text-muted-foreground">Ce mois-ci</span>
        }
      />
      <Stat
        icon={<Inbox size={16} />}
        label="Demandes"
        value={pendingDemandes.toString()}
        accent={pendingDemandes > 0 ? "primary" : "neutral"}
        footer={
          proposalSent > 0 ? (
            <span className="text-[10px] text-blue-700 font-semibold">
              + {proposalSent} chez le client
            </span>
          ) : pendingDemandes > 0 ? (
            <span className="text-[10px] text-primary font-semibold">
              À traiter
            </span>
          ) : (
            <span className="text-[10px] text-muted-foreground">À jour</span>
          )
        }
      />
      <Stat
        icon={<Star size={16} />}
        label="Note moyenne"
        value={averageRating > 0 ? averageRating.toFixed(1) : "—"}
        accent="amber"
        footer={
          reviewsCount > 0 ? (
            <span className="text-[10px] text-muted-foreground">
              {reviewsCount} avis
            </span>
          ) : (
            <span className="text-[10px] text-muted-foreground">
              Aucun avis
            </span>
          )
        }
      />
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  footer,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  footer?: React.ReactNode;
  accent: "primary" | "emerald" | "amber" | "neutral";
}) {
  const accentClasses = {
    primary: "bg-primary/10 text-primary",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    neutral: "bg-gray-100 text-gray-600",
  }[accent];

  return (
    <div className="bg-white rounded-2xl border border-border p-3.5 flex flex-col justify-between gap-2 min-h-[110px]">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center",
            accentClasses
          )}
        >
          {icon}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      </div>
      <p className="font-h3 text-dark-blue tabular-nums leading-none">
        {value}
      </p>
      <div className="min-h-[14px]">{footer}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Recent reviews
// ─────────────────────────────────────────────────────────────────────────

function RecentReviews({ feedbacks }: { feedbacks: Feedback[] }) {
  if (feedbacks.length === 0) {
    return (
      <section className="bg-white rounded-3xl border border-border p-6 md:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <span className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
            <MessageCircle size={20} />
          </span>
          <div>
            <h2 className="font-h4 text-dark-blue">
              Vos premiers avis arrivent bientôt
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Une fois vos prestations terminées, vos clients pourront laisser
              un retour. Ces témoignages s'afficheront ici.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-3xl border border-border p-5 md:p-6">
      <header className="mb-5 flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
          <MessageCircle size={18} />
        </span>
        <div>
          <h2 className="font-h4 text-dark-blue">Derniers avis</h2>
          <p className="text-xs text-muted-foreground">
            Ce que vos clients disent de vous
          </p>
        </div>
      </header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {feedbacks.slice(0, 3).map((feedback) => (
          <ReviewCard key={feedback.id} feedback={feedback} />
        ))}
      </div>
    </section>
  );
}

function ReviewCard({ feedback }: { feedback: Feedback }) {
  return (
    <article className="rounded-2xl border border-border p-4 bg-gradient-to-br from-[#fbf6f3] to-white">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              size={13}
              className={
                i < feedback.rating
                  ? "fill-amber-400 text-amber-400"
                  : "fill-gray-100 text-gray-200"
              }
            />
          ))}
        </div>
        <span className="text-[10px] text-muted-foreground">
          {formatDistanceToNow(new Date(feedback.createdAt), {
            locale: fr,
            addSuffix: true,
          })}
        </span>
      </div>
      <p className="text-sm text-dark-blue line-clamp-4 leading-relaxed mb-2 italic">
        “{feedback.comment}”
      </p>
      <p className="text-[11px] font-semibold text-muted-foreground">
        — {feedback.customerFirstName}
      </p>
    </article>
  );
}
